import { type Dispatch, type SetStateAction, memo, useMemo } from 'react';
import { Badge, Text } from '@mantine/core';
import type { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import type { SupplyStockReportItem } from 'washday-sdk';
import Table from '../../../components/Table';

export default memo(function TableView({
  tableInfoData,
  totalRowsCount,
  isLoading,
  isError,
  pagination,
  setPagination
}: {
  tableInfoData: SupplyStockReportItem[];
  totalRowsCount: number;
  isLoading: boolean;
  isError: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
}) {
  const { t, i18n } = useTranslation('ReportsPage');
  const language = i18n.resolvedLanguage || i18n.language;
  const columns = useMemo<MRT_ColumnDef<SupplyStockReportItem>[]>(() => {
    const numberFormat = new Intl.NumberFormat(language, { maximumFractionDigits: 3 });
    return [
      {
        accessorKey: 'storeName',
        header: t('ReportsPage:supplyStockReport.headers.store')
      },
      {
        accessorKey: 'name',
        header: t('ReportsPage:supplyStockReport.headers.supply')
      },
      {
        id: 'code',
        accessorFn: (row) => row.code || '—',
        header: t('ReportsPage:supplyStockReport.headers.code')
      },
      {
        accessorKey: 'purchaseUnit',
        header: t('ReportsPage:supplyStockReport.headers.unit')
      },
      {
        id: 'stock',
        accessorFn: (row) => numberFormat.format(row.stock),
        header: t('ReportsPage:supplyStockReport.headers.stock')
      },
      {
        id: 'alertOnStock',
        accessorFn: (row) =>
          row.alertOnStock == null ? '—' : numberFormat.format(row.alertOnStock),
        header: t('ReportsPage:supplyStockReport.headers.threshold')
      },
      {
        accessorKey: 'status',
        header: t('ReportsPage:supplyStockReport.headers.status'),
        Cell: ({ row: { original: item } }) => (
          <Badge
            variant="light"
            color={item.status === 'low' ? 'orange' : item.status === 'normal' ? 'green' : 'gray'}
          >
            {t(`ReportsPage:supplyStockReport.statuses.${item.status}`)}
          </Badge>
        )
      }
    ];
  }, [language, t]);

  return (
    <Table
      tableColumns={columns}
      tableData={tableInfoData}
      totalRowsCount={totalRowsCount}
      numPages={Math.max(1, Math.ceil(totalRowsCount / pagination.pageSize))}
      pagination={pagination}
      setPagination={setPagination}
      paginationIsLoading={isLoading}
      isError={isError}
      overrideTableConfigs={{
        renderEmptyRowsFallback: () => (
          <Text py="lg">{t('ReportsPage:supplyStockReport.empty')}</Text>
        ),
        mantinePaginationProps: {
          showRowsPerPage: false,
          getControlProps: (control: string) => ({
            'aria-label': t(`ReportsPage:supplyStockReport.pagination.${control}`)
          })
        }
      }}
    />
  );
});
